import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext.jsx';
import { Badge, Empty, Spinner, STATUS_TONE, PRIORITY_TONE, fmtDate, label } from '../components/ui.jsx';
import { useConfirm, useToast } from '../components/feedback.jsx';
import { t } from '../i18n/index.jsx';
import FundClientForm from '../components/forms/FundClientForm.jsx';
import SalForm, { blankSal } from '../components/forms/SalForm.jsx';
import TaskForm, { blankTask } from '../components/forms/TaskForm.jsx';
import DocumentList from '../components/DocumentList.jsx';
import { IconPlus } from '../components/icons.jsx';

const fmtMoney = (v) => (v == null || v === '' ? '—' : `€${Number(v).toLocaleString()}`);

export default function FundClientDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const confirm = useConfirm();
  const toast = useToast();
  const canEdit = user.role === 'admin' || user.role === 'manager';

  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [docSignal, setDocSignal] = useState(0);
  const [editEnroll, setEditEnroll] = useState(false);
  const [salModal, setSalModal] = useState(null);
  const [taskModal, setTaskModal] = useState(null);
  const [copying, setCopying] = useState(0);

  async function load() {
    try {
      const r = await api.get(`/fund-clients/${id}`);
      setData(r);
      const t = await api.get(`/documents?owner_type=fund&owner_id=${r.data.fund_id}`);
      setTemplates(t.data);
    } catch (e) { setErr(e.message); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => {
    if (canEdit) api.get('/users').then((r) => setUsers(r.data)).catch(() => {});
  }, [canEdit]);

  async function copyTemplate(tplId) {
    setCopying(tplId);
    try {
      await api.post(`/fund-clients/${id}/documents/from-template/${tplId}`);
      setDocSignal((n) => n + 1);
      toast(t('Document copied to client'));
    } catch (e) { toast(e.message, { tone: 'error' }); }
    finally { setCopying(0); }
  }

  async function genSalTasks(salId) {
    if (!(await confirm({ title: t('Generate tasks'), message: t('Create the standard checklist of tasks for this payment milestone?'), confirmLabel: t('Generate') }))) return;
    try {
      const r = await api.post(`/sal/${salId}/generate-tasks`);
      await load();
      toast(r.created === 1 ? t('1 task created') : t('{n} tasks created', { n: r.created }));
    } catch (e) { toast(e.message, { tone: 'error' }); }
  }

  if (err) return <div className="alert error">{err}</div>;
  if (!data) return <Spinner />;
  const fc = data.data;

  return (
    <div className="page fade-in">
      <div className="breadcrumb">
        <Link to="/funds">{t('Funds')}</Link> / <Link to={`/funds/${fc.fund_id}`}>{fc.fund_name}</Link> / {fc.client_name}
      </div>
      <div className="page-head">
        <div className="title-row">
          <h1 className="page-title">{fc.client_name}</h1>
          <Badge tone={STATUS_TONE[fc.status] || 'gray'}>{label(fc.status)}</Badge>
        </div>
        {canEdit && (
          <div className="head-actions">
            <button className="btn ghost" onClick={() => setEditEnroll(true)}>{t('Edit details')}</button>
          </div>
        )}
      </div>

      <div className="detail-grid">
        <div className="kv"><span>{t('Grant requested')}</span><b>{fmtMoney(fc.grant_requested)}</b></div>
        <div className="kv"><span>{t('Grant approved')}</span><b>{fmtMoney(fc.grant_approved)}</b></div>
        <div className="kv"><span>{t('Our fee')}</span><b>{fc.our_fee != null && fc.our_fee !== '' ? fmtMoney(fc.our_fee) : (fc.fee_pct ? `${fc.fee_pct}%` : '—')}</b></div>
        <div className="kv"><span>{t('Submitted')}</span><b>{fmtDate(fc.submitted_at)}</b></div>
        <div className="kv"><span>{t('Approved')}</span><b>{fmtDate(fc.approved_at)}</b></div>
      </div>
      {fc.notes && <p className="description">{fc.notes}</p>}

      {/* Duplicate master templates into this client's document set. */}
      {canEdit && (
        <section className="panel">
          <div className="panel-head"><h2 className="panel-title">{t('Copy from fund templates')}</h2></div>
          {templates.length === 0 ? <Empty>{t('No master templates on this fund yet.')}</Empty> : (
            <ul className="line-list">
              {templates.map((tpl) => (
                <li key={tpl.id}>
                  <div className="ll-main">
                    <span className="ll-title">{tpl.title}</span>
                    <span className="muted sm">{tpl.doc_type ? `${tpl.doc_type} · ` : ''}{tpl.original_name || t('no file')}</span>
                  </div>
                  <div className="ll-meta">
                    <button className="btn ghost sm" disabled={copying === tpl.id} onClick={() => copyTemplate(tpl.id)}>
                      {copying === tpl.id ? t('Copying…') : t('Copy to client')}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <DocumentList
        ownerType="fund_client" ownerId={fc.id} kind="instance"
        title={t('Client documents')} canEdit={canEdit} isAdmin={user.role === 'admin'} reloadSignal={docSignal}
      />

      <section className="panel">
        <div className="panel-head">
          <h2 className="panel-title">{t('Payment milestones (SAL)')}</h2>
          {canEdit && (
            <button className="btn ghost sm" onClick={() => setSalModal({ ...blankSal, fund_client_id: fc.id, seq: (data.sal.length || 0) + 1 })}>
              <IconPlus width={13} height={13} /> {t('Add milestone')}
            </button>
          )}
        </div>
        {data.sal.length === 0 ? <Empty>{t('No payment milestones yet. Use "Add milestone" to plan the first one.')}</Empty> : (
          <ul className="line-list">
            {data.sal.map((s) => (
              <li key={s.id} className={canEdit ? 'clickable' : ''} onClick={() => canEdit && setSalModal(s)}>
                <div className="ll-main">
                  <span className="ll-title">SAL {s.seq} · {s.title}</span>
                  <span className="muted sm">
                    {t('planned')} {fmtDate(s.planned_date)} · {fmtMoney(s.planned_amount)}
                    {s.paid_amount ? ` · ${t('paid')} ${fmtMoney(s.paid_amount)}` : ''}
                  </span>
                </div>
                <div className="ll-meta">
                  {canEdit && (
                    <button className="btn ghost sm" onClick={(e) => { e.stopPropagation(); genSalTasks(s.id); }}>{t('Generate tasks')}</button>
                  )}
                  <Badge tone={STATUS_TONE[s.status] || 'gray'}>{label(s.status)}</Badge>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="panel">
        <div className="panel-head">
          <h2 className="panel-title">{t('Tasks')}</h2>
          {canEdit && (
            <button className="btn ghost sm" onClick={() => setTaskModal({ ...blankTask, fund_client_id: fc.id })}>
              <IconPlus width={13} height={13} /> {t('Add task')}
            </button>
          )}
        </div>
        {data.tasks.length === 0 ? <Empty>{t('No tasks.')}</Empty> : (
          <ul className="line-list">
            {data.tasks.map((task) => (
              <li key={task.id} className={canEdit ? 'clickable' : ''} onClick={() => canEdit && setTaskModal(task)}>
                <div className="ll-main"><span className="ll-title">{task.title}</span></div>
                <div className="ll-meta">
                  <Badge tone={PRIORITY_TONE[task.priority]}>{label(task.priority)}</Badge>
                  <Badge tone={STATUS_TONE[task.status]}>{label(task.status)}</Badge>
                  <span className="due">{fmtDate(task.due_date)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {editEnroll && (
        <FundClientForm
          initial={fc}
          clients={[{ id: fc.company_id, name: fc.client_name }]}
          lockFund
          isAdmin={user.role === 'admin'}
          onClose={() => setEditEnroll(false)}
          onSaved={() => { setEditEnroll(false); load(); }}
        />
      )}
      {salModal && (
        <SalForm initial={salModal} onClose={() => setSalModal(null)} onSaved={() => { setSalModal(null); load(); }} />
      )}
      {taskModal && (
        <TaskForm
          initial={taskModal}
          users={users}
          onClose={() => setTaskModal(null)}
          onSaved={() => { setTaskModal(null); load(); }}
        />
      )}
    </div>
  );
}
